import {Component, EventEmitter, Input, Output} from '@angular/core';
import {CommonMessages} from '@app/constants/common.messages';
import { Globals } from '@app/shared/globals/globals';

export enum ButtonType {
  GREEN = 'GREEN',
  GREEN_INVERTED = 'GREEN_INVERTED',
  GRAY = 'GRAY',
  GRAY_INVERTED = 'GRAY_INVERTED',
  BLUE = 'BLUE',
  BLUE_INVERTED = 'BLUE_INVERTED',
  NAVY = 'NAVY',
  NAVY_INVERTED = 'NAVY_INVERTED',
  RED = 'RED',
  RED_INVERTED = 'RED_INVERTED',
  YELLOW = 'YELLOW',
  YELLOW_INVERTED = 'YELLOW_INVERTED',
  MICROSOFT_TEAMS = 'MICROSOFT_TEAMS',
  // Neutral white fill (gray text + light-gray outline). Colour only — shape is
  // controlled separately by ButtonShape.
  WHITE = 'WHITE',
  // Solid brand-colour fill with a white label — the primary call-to-action
  // (currently the login screen's "Log in with email"). The fill is exposed as a
  // CSS custom property (--button-brand-bg) so a per-domain theme can override it
  // at runtime when we switch branding, falling back to the product brand colour.
  BRAND = 'BRAND',
}

/**
 * Corner shape, independent of the colour ButtonType. Default is the fully
 * rounded pill; RECTANGLE is a rectangle with lightly rounded corners. Kept as a
 * separate axis so a shape change never touches a colour definition (or vice versa).
 */
export enum ButtonShape {
  PILL = 'PILL',
  RECTANGLE = 'RECTANGLE',
}

@Component({
  selector: 'app-button',
  templateUrl: './button.component.html',
  styleUrls: ['./button.component.scss']
})
export class ButtonComponent {
  public readonly eCommonMessages = CommonMessages;
  public readonly eButtonType = ButtonType;

  @Input() type: ButtonType;
  @Input() shape: ButtonShape;

  // Optional leading icon: a brand logo image (iconSrc, e.g. the Google/Microsoft
  // PNGs) or a font-awesome class (iconClass). Both are optional — a button with
  // neither is just a plain label.
  @Input() iconSrc?: string;
  @Input() iconAlt = '';
  @Input() iconClass?: string;

  @Input() loading: boolean;
  @Input() disabled: boolean;

  @Input() small: boolean;

  @Input() tooltipText: string;
  @Input() tooltipPlacement: string;
  @Input() tooltipContainer: string;
  @Input() tooltipTriggers: string;

  @Input() buttonWidth: string;

  @Output() onClick: EventEmitter<MouseEvent>;

  constructor(
    public globals: Globals
  ) {
    this.type = ButtonType.GREEN;
    this.shape = ButtonShape.PILL;

    this.loading = false;
    this.disabled = false;
    this.small = false;

    this.tooltipText = '';
    this.tooltipContainer = 'body';
    this.tooltipPlacement = 'bottom';
    this.tooltipTriggers = this.globals.tooltipTriggers;

    this.buttonWidth = 'unset';

    this.onClick = new EventEmitter<MouseEvent>();
  }

  clickButton(event: MouseEvent): void {
    if (this.loading) { return; }
    if (this.disabled) { return; }
    this.onClick.emit(event);
  }
}
